<template>
	<div class="flex justify-start flex-row">
		<div
			class="form-group flex relative flex flex-grow"
			:class="{ 'flex-col': true, 'items-center flex-row-reverse': false }"
			v-click-outside="closeMenu"
			:title="title"
		>
			<div class="w-full flex justify-between" v-if="label">
				<label>
					{{ label }}
					<span v-if="!disabled && required" class="text-red-600 font-bold"> *</span>
				</label>
			</div>
			<div class="wrapper w-full mb-3">
				<div class="inputgroup w-full">
					<font-awesome-icon
						v-if="displayValue !== ''"
						class="innericon cursor-pointer"
						style="align-self: end;"
						:icon="['fas', 'trash']"
						@click="removeInput"
					/>
					<input
						type="text"
						ref="BaseInput"
						class="base-input flex flex-grow cursor-pointer"
						@change="$emit('change', $event)"
						@keyup="$emit('keyup', $event)"
						:class="{ 'w-full': true }"
						@click="openMenu"
						v-model="displayValue"
						:placeholder="label"
						:disabled="disabled"
						:readonly="true"
					/>
				</div>
			</div>

			<div class="input-errors" v-if="!isValid && !disabled">
				{{ validationMessage || getDefaultErrorsMessage }}
			</div>
			<div class="text-sm">
				<div v-if="showMenu" class="absolute bg-white z-50 rounded w-full">
					<div class="flex flex-grow justify-between text-lg pb-2 items-center">
						<div>
							<BaseButton class="flex-shrink" style="min-width: 80px" @click="previousYear">
								<font-awesome-icon :icon="['fas', 'angle-left']" />
							</BaseButton>
						</div>
						<div class="flex font-bold">
							{{ year || currentYear }}
						</div>
						<div>
							<BaseButton class="flex-shrink" style="min-width: 80px" @click="nextYear">
								<font-awesome-icon :icon="['fas', 'angle-right']" />
							</BaseButton>
						</div>
					</div>
					<div class="grid grid-cols-3 border w-full">
						<div
							v-for="monthData in months"
							:key="monthData.value"
							class="cursor-pointer hover:bg-connectProBlue hover:text-white p-3 border text-center"
							@mousedown="updateValue(monthData)"
							:class="{ 'bg-connectProBlue text-white': month && month.value === monthData.value }"
						>
							{{ monthData.display }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton';
import { getTimeWithLeadingNull } from '@/plugins/Utils';
export default {
	name: 'YearMonthInput',
	components: { BaseButton },
	props: {
		value: {},
		label: {},
		required: {},
		title: { type: String },
		disabled: {},
	},
	data() {
		return {
			year: null,
			month: null,
			showMenu: false,
			validationTypeValidation: null,
			validationMessage: null,
			isValid: true,
		};
	},
	methods: {
		removeInput() {
			this.year = null;
			this.month = null;
			this.$emit('input', this.formattedValue);
		},
		closeMenu() {
			this.showMenu = false;
		},
		openMenu() {
			this.showMenu = true;
			this.$refs.BaseInput.focus();
		},
		updateValue(month) {
			this.year = this.year || this.currentYear;
			this.month = month;
			this.closeMenu();
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.$emit('input', this.formattedValue);
		},
		nextYear() {
			this.year = this.year || this.currentYear;
			this.year++;
			this.$emit('input', this.formattedValue);
		},
		previousYear() {
			this.year = this.year || this.currentYear;
			this.year--;
			this.$emit('input', this.formattedValue);
		},
	},
	computed: {
		// TODO make definable via prop
		displayValue() {
			if (!this.month || !this.year) return '';
			return `${getTimeWithLeadingNull(this.month.value)}/${this.year?.toString().slice(-2)}`;
		},
		formattedValue() {
			if (!this.month || !this.year) return '';
			return `${this.year}-${getTimeWithLeadingNull(this.month.value)}`;
		},
		getDefaultErrorsMessage: function() {
			if (this.label && this.required) {
				return this.label + ' ' + this.$t('is_required');
			}
			return this.$t('field_not_validated').toString();
		},
		currentYear() {
			return new Date().getFullYear();
		},
		months() {
			return [
				{
					value: 1,
					display: 'Januar',
				},
				{
					value: 2,
					display: 'Februar',
				},
				{
					value: 3,
					display: 'März',
				},
				{
					value: 4,
					display: 'April',
				},
				{
					value: 5,
					display: 'Mai',
				},
				{
					value: 6,
					display: 'Juni',
				},
				{
					value: 7,
					display: 'Juli',
				},
				{
					value: 8,
					display: 'August',
				},
				{
					value: 9,
					display: 'September',
				},
				{
					value: 10,
					display: 'Oktober',
				},
				{
					value: 11,
					display: 'November',
				},
				{
					value: 12,
					display: 'Dezember',
				},
			];
		},
	},
	watch: {
		// if value from outside is change, apply the changes to year and month
		value: {
			immediate: true,
			handler() {
				if (!this.value) {
					this.month = null;
					this.year = null;
				} else {
					const split = this.value.toString().split('-');

					this.year = parseInt(split[0]);
					this.month = this.months.find(e => e.value === parseInt(split[1]));
				}
			},
		},
	},
};
</script>

<style scoped>
.wrapper {
	width: 100%;
}
* {
	box-sizing: border-box;
}
.inputgroup {
	position: relative;
	display: inline-block;
	height: 30px;
	line-height: 30px;
	vertical-align: middle;
}
.inputgroup .innericon {
	position: absolute;
	top: 12px;
	right: 16px;
	z-index: 2;
	display: block;
	text-align: center;
}
.base-input {
	@apply outline-none;
	height: 43px;
}

.form-group label {
	word-break: break-word;
}

.form-group.cursor-pointer label {
	@apply cursor-pointer;
}

.input-errors {
	@apply break-words;
}
</style>
